import { useState } from 'react'
import type { PaasValues } from '@/types/infrastructure'
import { validatePaas, visiblePaasFields, type PaasField } from '@/utils/infraPaasFields'
import page from '@/pages/NewDeploymentPage.module.css'
import { OptionCard } from './OptionCard'
import { TextField } from './TextField'
import styles from './Infrastructure.module.css'
import clsx from 'clsx'

interface PaasFormProps {
  values: PaasValues
  onChange: (values: PaasValues) => void
  idPrefix: string
}

/**
 * The PaaS SQL server form, drawn from the field table in infraPaasFields.ts. Add or remove a field there and it appears
 * or disappears here. A field's message appears once the user has left it, so an untouched form is not covered in errors.
 */
export function PaasForm({ values, onChange, idPrefix }: PaasFormProps) {
  const [touched, setTouched] = useState<Set<string>>(new Set())
  const errors = validatePaas(values)
  const touch = (key: string) => setTouched((prev) => new Set(prev).add(key))
  const problem = (field: PaasField) => (touched.has(field.key) ? (errors[field.key] ?? null) : null)
  const set = (key: string, value: string) => onChange({ ...values, [key]: value })

  const render = (field: PaasField) => {
    const id = `${idPrefix}-${field.key}`
    const value = values[field.key] ?? ''
    if (field.type === 'radio') {
      return (
        <fieldset key={field.key} className={styles.cardGroup} style={{ gridColumn: '1 / -1' }}>
          <legend className={styles.legend}>{field.label}</legend>
          {(field.options ?? []).map((option) => (
            <OptionCard key={option.value} name={id} value={option.value} checked={value === option.value} onChange={(next) => set(field.key, next)} title={option.label} />
          ))}
        </fieldset>
      )
    }
    if (field.type === 'select') {
      return (
        <div key={field.key} className={page.formGroup}>
          <label htmlFor={id} className={page.label}>
            {field.label} {field.required && <span className={page.required}>*</span>}
          </label>
          <select id={id} className={clsx(page.select, problem(field) && page.inputError)} value={value} onChange={(event) => set(field.key, event.target.value)} onBlur={() => touch(field.key)} aria-invalid={problem(field) ? true : undefined}>
            {(field.options ?? []).map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          {field.help && <span className={page.fieldHint}>{field.help}</span>}
          {problem(field) && (
            <span role="alert" className={page.errorMsg}>
              {problem(field)}
            </span>
          )}
        </div>
      )
    }
    if (field.type === 'textarea') {
      return (
        <div key={field.key} className={page.formGroup} style={{ gridColumn: '1 / -1' }}>
          <label htmlFor={id} className={page.label}>
            {field.label} {field.required && <span className={page.required}>*</span>}
          </label>
          <textarea id={id} className={clsx(page.textarea, problem(field) && page.inputError)} rows={3} value={value} onChange={(event) => set(field.key, event.target.value)} onBlur={() => touch(field.key)} aria-invalid={problem(field) ? true : undefined} />
          {field.help && <span className={page.fieldHint}>{field.help}</span>}
          {problem(field) && (
            <span role="alert" className={page.errorMsg}>
              {problem(field)}
            </span>
          )}
        </div>
      )
    }
    return (
      <TextField
        key={field.key}
        id={id}
        label={field.label}
        required={field.required}
        type={field.type === 'password' ? 'password' : field.type === 'number' ? 'number' : 'text'}
        value={value}
        onChange={(next) => set(field.key, next)}
        onBlur={() => touch(field.key)}
        hint={field.help}
        problem={problem(field)}
        autoComplete={field.type === 'password' ? 'new-password' : 'off'}
      />
    )
  }

  return <div className={page.formGrid}>{visiblePaasFields(values).map(render)}</div>
}
